import React, { useEffect, useRef, useState } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormLabel from "components/Extra/FormLabel"
import { Dispute } from "pages/admin/Disputes/Disputes"
import { FormInput } from "components/Extra/FormInput"
import { useTranslation } from "react-i18next"

interface ChangeStatusDiputeModalProps {
  dispute: Dispute | null
  isOpen: boolean
  toggle: () => void
  currentView: "respond" | "rejected"
}

export const ChangeStatusDiputeModal: React.FC<
  ChangeStatusDiputeModalProps
> = ({ currentView, isOpen, toggle }) => {
  const { t } = useTranslation()

  const [openAccordion, setOpenAccordion] = useState("0")
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      message: "",
      attachments: [] as Array<{
        id: string
        name: string
        file?: File
        preview?: string
      }>,
    },
    validationSchema: Yup.object({
      // message: Yup.string().trim(),
      attachments: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(
            t("disputes_idValidationMessage") as string
          ),
          name: Yup.string().required(
            t("disputes_nameValidationMessage") as string
          ),
        })
      ),
    }),
    onSubmit: ({ attachments, message }) => {
      console.log({ attachments, message })

      if (currentView === "respond") {
        // TODO: Dispatch for respond
      } else {
        // TODO: Dispatch for rejected
      }

      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    validation.resetForm()
  }, [isOpen])

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const files = Array.from(e.target.files).map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      file,
      preview: URL.createObjectURL(file),
    }))
    validation.setFieldValue("attachments", [
      ...validation.values.attachments,
      ...files,
    ])
  }

  const handleRemove = (id: string) => {
    validation.setFieldValue(
      "attachments",
      validation.values.attachments.filter(att => att.id !== id)
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {currentView === "respond"
          ? t("disputes_respondToDispute")
          : t("disputes_rejectDispute")}
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Accordion open={openAccordion}>
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenAccordion(openAccordion === "0" ? "1" : "0")
                }
              >
                {currentView === "respond"
                  ? t("common_respondInfo")
                  : t("common_rejectedInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormInput
                  label={
                    currentView === "respond"
                      ? (t("disputes_respondMessage") as string)
                      : (t("disputes_rejectedMessage") as string)
                  }
                  name="message"
                  type="textarea"
                  placeholder={t("disputes_enterRespondMessage") as string}
                  validation={validation}
                  rows="3"
                />

                <div className="mb-3">
                  <FormLabel className="form-label" htmlFor="note">
                    {t("common_attachments")}
                  </FormLabel>
                  <div
                    className="d-flex justify-content-between align-items-center px-1"
                    style={{ cursor: "pointer" }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <InputGroup>
                      <input
                        type="number"
                        className="form-control"
                        placeholder={t("common_attachments") as string}
                        readOnly
                      />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFilesChange}
                        placeholder={t("common_attachments") as string}
                        className="form-control"
                      />
                      <span className="input-group-append">
                        <i className="bx bx-upload font-size-20 input-group-text"></i>
                      </span>
                    </InputGroup>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFilesChange}
                  />
                </div>

                {/* Image preview */}
                {validation.values.attachments.length > 0 && (
                  <div className="d-flex flex-wrap gap-2">
                    {validation.values.attachments.map(att => (
                      <div
                        key={att.id}
                        className="position-relative"
                        style={{
                          width: "80px",
                          height: "80px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={att.preview}
                          alt={att.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Button
                          close
                          onClick={() => handleRemove(att.id)}
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            background: "white",
                            borderRadius: "50%",
                            padding: "0",
                            width: "20px",
                            height: "20px",
                          }}
                        >
                          <i className="mdi mdi-close"></i>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </AccordionBody>
            </AccordionItem>
          </Accordion>
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn btn-primary save-customer`}
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {t("common_send")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_create")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
